<template>
    <b-container fluid class="px-md-5">
        <b-row class="mt-4 mb-2 align-items-center">
            <b-col cols="auto">
                <b-button variant="link" class="text-primary p-0" @click="$router.push({ name: 'ComplianceIndex' })">
                    <i class="material-icons">arrow_back</i>
                </b-button>
            </b-col>
            <b-col>
                <h4 class="mb-0 text-primary text-uppercase">Detalle de Remitente — Cumplimiento</h4>
            </b-col>
        </b-row>

        <div v-if="loading" class="text-center my-5"><b-spinner variant="primary"></b-spinner></div>

        <template v-else-if="data">
            <!-- Ficha del remitente -->
            <b-card class="mb-3 shadow-sm border-0">
                <b-row>
                    <b-col cols="12" md="6">
                        <h6 class="subtitle">Información del Remitente</h6>
                        <table class="table table-sm table-borderless">
                            <tbody>
                                <tr><td class="text-muted">Nombre</td><td><strong>{{ data.sender.name }} {{ data.sender.lname }}</strong></td></tr>
                                <tr><td class="text-muted">Documento</td><td>{{ data.sender.td }} — {{ data.sender.nd }}</td></tr>
                                <tr><td class="text-muted">Email</td><td>{{ data.sender.email || '—' }}</td></tr>
                                <tr><td class="text-muted">Teléfono</td><td>{{ data.sender.mobile || '—' }}</td></tr>
                                <tr><td class="text-muted">Dirección</td><td>{{ data.sender.address || '—' }}, {{ data.sender.city || '' }}</td></tr>
                                <tr><td class="text-muted">País origen</td><td>{{ data.sender.country || '—' }}</td></tr>
                            </tbody>
                        </table>
                    </b-col>
                    <b-col cols="12" md="6">
                        <h6 class="subtitle">Datos KYC</h6>
                        <table class="table table-sm table-borderless">
                            <tbody>
                                <tr><td class="text-muted">Fecha Nac.</td><td>{{ data.sender.birthday || '—' }}</td></tr>
                                <tr><td class="text-muted">Profesión</td><td>{{ data.sender.profession || '—' }}</td></tr>
                                <tr>
                                    <td class="text-muted">PEP</td>
                                    <td>
                                        <b-badge :variant="data.sender.pep && data.sender.pep !== '0' ? 'danger' : 'success'">
                                            {{ data.sender.pep && data.sender.pep !== '0' ? 'SÍ — Persona Expuesta Políticamente' : 'No' }}
                                        </b-badge>
                                    </td>
                                </tr>
                                <tr><td class="text-muted">Último envío</td><td>{{ formatDate(data.stats.last_payment_at) }}</td></tr>
                            </tbody>
                        </table>
                    </b-col>
                </b-row>
            </b-card>

            <!-- Stats -->
            <b-row class="mb-3">
                <b-col cols="6" md="3" class="mb-2">
                    <b-card class="text-center shadow-sm border-0 h-100" body-class="py-3">
                        <h3 class="mb-0 text-primary">{{ data.stats.payments_6m || 0 }}</h3>
                        <small class="text-muted">Envíos (6 meses)</small>
                    </b-card>
                </b-col>
                <b-col cols="6" md="3" class="mb-2">
                    <b-card class="text-center shadow-sm border-0 h-100" body-class="py-3">
                        <h3 class="mb-0" :class="(data.stats.total_usd_6m || 0) > 4999 ? 'text-danger' : 'text-success'">
                            USD {{ formatMoney(data.stats.total_usd_6m) }}
                        </h3>
                        <small class="text-muted">Acumulado 6 meses</small>
                    </b-card>
                </b-col>
                <b-col cols="6" md="3" class="mb-2">
                    <b-card class="text-center shadow-sm border-0 h-100" body-class="py-3">
                        <h3 class="mb-0 text-info">{{ data.stats.total_payments || 0 }}</h3>
                        <small class="text-muted">Envíos totales</small>
                    </b-card>
                </b-col>
                <b-col cols="6" md="3" class="mb-2">
                    <b-card class="text-center shadow-sm border-0 h-100" body-class="py-3">
                        <h3 class="mb-0 text-secondary">USD {{ formatMoney(data.stats.total_usd) }}</h3>
                        <small class="text-muted">Acumulado histórico</small>
                    </b-card>
                </b-col>
            </b-row>

            <!-- Alertas -->
            <b-card class="mb-3 shadow-sm border-0" v-if="data.alerts && data.alerts.length">
                <h6 class="subtitle text-danger">Alertas de Cumplimiento ({{ data.alerts.length }})</h6>
                <b-table :items="data.alerts" :fields="alertFields" responsive="sm" small hover table-variant="light">
                    <template #cell(alert_type)="row">
                        <b-badge :variant="row.item.alert_type === 'single_transaction' ? 'warning' : 'info'">
                            {{ row.item.alert_type === 'single_transaction' ? 'Individual' : 'Acumulado' }}
                        </b-badge>
                    </template>
                    <template #cell(amount_usd)="row">
                        <strong class="text-danger">USD {{ formatMoney(row.item.amount_usd) }}</strong>
                    </template>
                    <template #cell(threshold)="row">USD {{ formatMoney(row.item.threshold) }}</template>
                    <template #cell(status)="row">
                        <b-badge :variant="row.item.status === 0 ? 'danger' : row.item.status === 1 ? 'success' : 'secondary'">
                            {{ row.item.status === 0 ? 'Nueva' : row.item.status === 1 ? 'Revisada' : 'Descartada' }}
                        </b-badge>
                    </template>
                    <template #cell(created_at)="row"><small>{{ formatDate(row.item.created_at) }}</small></template>
                    <template #cell(notes)="row"><small class="text-muted">{{ row.item.notes || '—' }}</small></template>
                </b-table>
            </b-card>

            <!-- Historial de pagos -->
            <b-card class="shadow-sm border-0">
                <h6 class="subtitle">Historial de Transacciones</h6>
                <b-table :items="payments" :fields="paymentFields" responsive="sm" small hover show-empty
                    :busy="loadingPayments" empty-text="Sin transacciones" table-variant="light">
                    <template #cell(id)="row"><small class="text-muted">#{{ row.item.id }}</small></template>
                    <template #cell(amount_send)="row">
                        <strong>{{ formatMoney(row.item.amount_send) }} {{ row.item.currency_send }}</strong>
                    </template>
                    <template #cell(amount_receiver)="row">
                        {{ formatMoney(row.item.amount_receiver) }} {{ row.item.currency_receiver }}
                    </template>
                    <template #cell(receiver)="row">
                        <small>{{ parseReceiver(row.item.receiver) }}</small>
                    </template>
                    <template #cell(status)="row">
                        <b-badge :variant="row.item.status === 109 ? 'success' : 'info'">{{ row.item.status }}</b-badge>
                    </template>
                    <template #cell(created_at)="row"><small>{{ formatDate(row.item.created_at) }}</small></template>
                    <template #table-busy>
                        <div class="text-center text-primary my-3"><b-spinner class="align-middle mr-1"></b-spinner></div>
                    </template>
                </b-table>
                <b-pagination v-model="paymentPage" :total-rows="paymentTotal" :per-page="20"
                    align="center" size="sm" class="mt-2" @change="loadPayments"></b-pagination>
            </b-card>
        </template>

        <b-alert v-else show variant="warning">No se encontró el remitente.</b-alert>
    </b-container>
</template>

<script>
import axios from 'axios';

export default {
    name: 'ComplianceSenderDetail',
    data: () => ({
        loading: true,
        data: null,
        alertFields: [
            { key: 'alert_type', label: 'Tipo' },
            { key: 'threshold',  label: 'Umbral' },
            { key: 'amount_usd', label: 'Monto USD' },
            { key: 'status',     label: 'Estado' },
            { key: 'created_at', label: 'Fecha' },
            { key: 'notes',      label: 'Notas' },
        ],
        payments: [],
        paymentFields: [
            { key: 'id',              label: 'ID' },
            { key: 'created_at',      label: 'Fecha' },
            { key: 'amount_send',     label: 'Enviado' },
            { key: 'amount_receiver', label: 'Recibido' },
            { key: 'receiver',        label: 'Destinatario' },
            { key: 'country_receiver', label: 'País' },
            { key: 'payer_id',        label: 'Pagador' },
            { key: 'status',          label: 'Estado' },
        ],
        paymentPage: 1, paymentTotal: 0, loadingPayments: false,
    }),
    mounted() {
        this.loadData();
    },
    methods: {
        loadData() {
            this.loading = true;
            const nd = this.$route.query.nd;
            axios.get('/api/compliance/senders/detail', { params: { nd } }).then(r => {
                this.data = r.data;
                this.payments = r.data.payments.data;
                this.paymentTotal = r.data.payments.total;
            }).finally(() => { this.loading = false; });
        },
        loadPayments(page) {
            this.loadingPayments = true;
            const nd = this.$route.query.nd;
            axios.get('/api/compliance/senders/detail', { params: { nd, page } }).then(r => {
                this.payments = r.data.payments.data;
                this.paymentTotal = r.data.payments.total;
                this.paymentPage = page;
            }).finally(() => { this.loadingPayments = false; });
        },
        formatMoney(val) {
            return Number(val || 0).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatDate(val) {
            if (!val) return '—';
            return new Date(val).toLocaleString('es-CL');
        },
        parseReceiver(raw) {
            try {
                const r = typeof raw === 'string' ? JSON.parse(raw) : raw;
                return `${r.name || ''} ${r.lname || ''}`.trim();
            } catch { return '—'; }
        },
    },
};
</script>
